import { motion } from "framer-motion";
import { Smartphone, MapPin, Bell, Clock, Route, ChevronRight } from "lucide-react";

const AppDownloadSection = () => {
  return (
    <section id="app" className="py-16 sm:py-24 bg-sarathi-background relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-sarathi-primary/8 rounded-full blur-[150px] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-sarathi-primary/10 border border-sarathi-primary/20 rounded-full px-4 py-1.5 mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sarathi-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-sarathi-primary"></span>
              </span>
              <span className="text-sarathi-primary text-xs font-semibold tracking-wide uppercase">Coming Soon</span>
            </div>

            <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4">
              Sarathi-X App
            </h2>
            <p className="text-sarathi-text-secondary text-base mb-3 leading-relaxed max-w-md">
              Real-time tracking, instant confirmations, and trip history — all in your pocket.
            </p>
            <p className="text-sarathi-text-muted text-sm mb-8">
              Android launching first. iOS to follow.
            </p>

            {/* WhatsApp Notify CTA */}
            <a
              href="https://wa.me/919187057303?text=Hi%2C%20I%27d%20like%20to%20be%20notified%20when%20the%20Sarathi-X%20app%20launches."
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/30 hover:border-[#25D366]/50 rounded-full pl-5 pr-4 py-3 transition-all duration-300"
              data-testid="app-notify-cta"
            >
              <svg className="w-5 h-5 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              <span className="text-[#25D366] font-medium text-sm">Get notified on WhatsApp</span>
              <ChevronRight className="w-4 h-4 text-[#25D366]/60 group-hover:translate-x-0.5 transition-transform" />
            </a>
          </motion.div>

          {/* Phone Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="relative flex justify-center"
          >
            <div className="relative">
              {/* Phone Frame */}
              <div className="w-[200px] sm:w-[220px] bg-sarathi-surface rounded-[2rem] p-2 border border-white/10 shadow-2xl">
                <div className="bg-sarathi-background rounded-[1.75rem] overflow-hidden">
                  {/* Notch */}
                  <div className="flex justify-center pt-2.5 pb-0.5">
                    <div className="w-16 h-4 bg-sarathi-surface rounded-full"></div>
                  </div>

                  {/* App Screen — Feature Preview */}
                  <div className="aspect-[9/16] px-3 py-2.5 flex flex-col">
                    {/* App header */}
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-7 h-7 bg-gradient-to-br from-sarathi-primary to-orange-600 rounded-lg flex items-center justify-center">
                        <Smartphone className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div>
                        <p className="text-white font-outfit font-semibold text-xs leading-tight">Sarathi-X</p>
                        <p className="text-sarathi-text-muted text-[8px]">Premium EV Mobility</p>
                      </div>
                    </div>

                    {/* Feature rows */}
                    <div className="space-y-2 flex-1">
                      {[
                        { icon: MapPin, label: "Live tracking", color: "text-sarathi-primary" },
                        { icon: Bell, label: "Instant alerts", color: "text-sarathi-primary" },
                        { icon: Clock, label: "Trip history", color: "text-sarathi-primary" },
                        { icon: Route, label: "Fixed fare", color: "text-sarathi-primary" },
                      ].map((f, i) => (
                        <motion.div
                          key={f.label}
                          initial={{ opacity: 0, x: 12 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.35, delay: 0.35 + i * 0.1 }}
                          className="flex items-center gap-2 bg-white/[0.04] rounded-lg px-2.5 py-2 border border-white/5"
                        >
                          <div className="w-6 h-6 rounded-md bg-sarathi-primary/10 flex items-center justify-center flex-shrink-0">
                            <f.icon className={`w-3 h-3 ${f.color}`} />
                          </div>
                          <span className="text-white text-[10px] font-medium">{f.label}</span>
                        </motion.div>
                      ))}
                    </div>

                    {/* Bottom bar */}
                    <div className="flex justify-center gap-3 pt-2 pb-1 mt-auto">
                      <div className="w-6 h-0.5 bg-white/10 rounded-full"></div>
                      <div className="w-6 h-0.5 bg-sarathi-primary/40 rounded-full"></div>
                      <div className="w-6 h-0.5 bg-white/10 rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative */}
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-sarathi-primary/20 rounded-full blur-xl pointer-events-none"></div>
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-sarathi-primary/10 rounded-full blur-2xl pointer-events-none"></div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AppDownloadSection;
